import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CalculateIcon from '@mui/icons-material/Calculate';
import PaletteIcon from '@mui/icons-material/Palette';
import PublicIcon from '@mui/icons-material/Public';

// ─── Color helpers (equal to CampaignTracker logic) ──────────────────────────
const getCellBgColor = (val) => {
  if (val === 100) return '#4CAF50';
  if (val >= 50)   return '#C8E6C9';
  if (val >= 1)    return '#FFE0B2';
  return '#FFCDD2';
};

const getCellTextColor = (val) => (val === 100 ? '#fff' : 'inherit');

// ─── Reusable labeled tooltip ─────────────────────────────────────────────────
const Term = ({ label, tip }) => (
  <Tooltip title={tip} arrow placement="top">
    <Box
      component="span"
      sx={{
        borderBottom: '1px dashed',
        borderColor: 'primary.main',
        cursor: 'help',
        fontWeight: 'bold',
        color: 'primary.main',
      }}
    >
      {label}
    </Box>
  </Tooltip>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
    <Typography variant="h6">{title}</Typography>
  </Box>
);

// ─── Color legend row ─────────────────────────────────────────────────────────
const COLOR_LEGEND = [
  { range: '100%',    bg: '#4CAF50', text: '#fff',     label: 'Implementación completa' },
  { range: '50 – 99%', bg: '#C8E6C9', text: '#1b5e20', label: 'Mayoría implementada' },
  { range: '1 – 49%', bg: '#FFE0B2', text: '#e65100', label: 'Implementación parcial' },
  { range: '0%',      bg: '#FFCDD2', text: '#b71c1c', label: 'Sin implementar' },
];

// ─── Example data (new % system) ─────────────────────────────────────────────
// Each country has 8 valid channels. Each channel has an implementation_percentage.
const EXAMPLE_COUNTRIES = [
  {
    country: 'México',
    channels: [100, 80, 60, 100, 45, 70, 90, 55],
  },
  {
    country: 'Argentina',
    channels: [100, 0, 60, 0, 50, 0, 0, 80],
  },
  {
    country: 'Colombia',
    channels: [30, 0, 0, 0, 20, 0, 0, 0],
  },
  {
    country: 'Chile',
    channels: [100, 75, 0, 100, 0, 50, 85, 0],
  },
];

const buildCountryStats = (entry) => {
  const total = entry.channels.length;
  const activeCount = entry.channels.filter((v) => v >= 50).length;
  const avg = Math.round(entry.channels.reduce((s, v) => s + v, 0) / total);
  const coverageRatio = (activeCount / total) * 100;
  const implemented = coverageRatio >= 50;
  return { ...entry, total, activeCount, avg, coverageRatio, implemented };
};

const EXAMPLE_STATS = EXAMPLE_COUNTRIES.map(buildCountryStats);

// ─── Component ────────────────────────────────────────────────────────────────
const CampaignCompleteness = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const implementedCount = EXAMPLE_STATS.filter((s) => s.implemented).length;
  const globalAvg = Math.round(
    EXAMPLE_STATS.reduce((s, c) => s + c.avg, 0) / EXAMPLE_STATS.length
  );

  return (
    <Box>
      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>
        ¿Cómo funciona el % de Implementación?
      </Typography>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3 }}
      >
        Esta vista explica cómo registrar avances en el tracker y cómo el sistema
        calcula automáticamente la completitud de cada campaña por país.
      </Alert>

      <Grid container spacing={3}>

        {/* ── 1. QUÉ ES EL PORCENTAJE ─────────────────────────────── */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <SectionHeader icon={<EditNoteIcon />} title="1 · ¿Qué ingreso en cada celda?" />
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" paragraph>
              En el <strong>Tracker de ToolBox</strong> cada celda representa{' '}
              <Term
                label="un canal en un país"
                tip="Por ejemplo: Instagram en México, o Facebook en Colombia."
              />
              . Debes ingresar un número del <strong>0 al 100</strong> que refleje
              qué tanto está implementado ese canal para la campaña en ese país.
            </Typography>

            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Ejemplos de valores:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <li>
                  <Typography variant="body2">
                    <strong>0</strong> — El canal no está implementado en ese país.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>50</strong> — El canal está implementado a la mitad
                    (ej. contenido creado pero sin publicar).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>100</strong> — El canal está completamente implementado
                    y activo en ese país.
                  </Typography>
                </li>
              </Box>
            </Box>

            {/* Color legend */}
            <SectionHeader icon={<PaletteIcon />} title="¿Qué significa cada color?" />
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {COLOR_LEGEND.map(({ range, bg, text, label }) => (
                <Box
                  key={range}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    backgroundColor: bg,
                    minWidth: 180,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: text, minWidth: 52 }}
                  >
                    {range}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ── 2. CÓMO SE CALCULA ──────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <SectionHeader icon={<CalculateIcon />} title="2 · ¿Cómo se calcula la completitud?" />
            <Divider sx={{ mb: 2 }} />

            {/* Step A */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                A · Canales válidos por país
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Solo se cuentan los canales que están activos tanto para{' '}
                <Term
                  label="el país"
                  tip="Configurado en Administración › Canales por País."
                />
                {' '}como para{' '}
                <Term
                  label="la ToolBox"
                  tip="Configurado en Administración › Canales por ToolBox."
                />
                . Un canal inactivo en cualquiera de los dos no entra al cálculo.
              </Typography>
            </Box>

            {/* Step B */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                B · ¿Cuándo un país está "implementado"?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Un país se considera implementado cuando{' '}
                <strong>al menos el 50% de sus canales válidos</strong> tienen un
                porcentaje <strong>mayor o igual a 50</strong>.
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#e8f5e9',
                  border: '2px solid #4caf50',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="#2e7d32">
                  Canales con % ≥ 50 / Canales válidos ≥ 50% → ✓ Implementado
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Nota: un canal con 49% no cuenta. El umbral es ≥ 50% por canal.
                El promedio global (paso C) sí refleja todos los valores exactos.
              </Typography>
            </Box>

            {/* Step C */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                C · Promedio de implementación
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                El promedio es la media aritmética de{' '}
                <em>todos</em> los porcentajes de los canales válidos de todos los países:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="body2">
                  % Promedio = SUMA(porcentajes) / Total de canales válidos
                </Typography>
              </Box>
            </Box>

            {/* Step D */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                D · Cobertura de la campaña
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                La{' '}
                <Term
                  label="cobertura"
                  tip="Métrica visible en el Dashboard. Dice qué fracción de los países participantes superó el umbral del 50%."
                />
                {' '}refleja qué porcentaje de países están implementados:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="body2">
                  Cobertura = Países implementados / Total de países × 100
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── 3. EJEMPLO VISUAL ───────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
            <SectionHeader icon={<PublicIcon />} title="3 · Ejemplo práctico" />
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" paragraph>
              ToolBox con <strong>8 canales válidos</strong> por país:
            </Typography>

            {EXAMPLE_STATS.map((item) => (
              <Box
                key={item.country}
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: `2px solid ${item.implemented ? '#4caf50' : '#f44336'}`,
                  backgroundColor: item.implemented ? '#f1f8e9' : '#fff8f8',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {item.country}
                  </Typography>
                  {item.implemented ? (
                    <Tooltip title="≥ 50% de canales con % > 0" arrow>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                        <Typography variant="caption" color="#2e7d32" fontWeight="bold">
                          Implementado
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Menos del 50% de canales con % > 0" arrow>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CancelIcon sx={{ color: '#f44336', fontSize: 18 }} />
                        <Typography variant="caption" color="#c62828" fontWeight="bold">
                          No implementado
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Mini channel grid */}
                <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                  {item.channels.map((v, i) => (
                    <Tooltip key={i} title={`Canal ${i + 1}: ${v}%`} arrow>
                      <Box
                        sx={{
                          width: 28,
                          height: 22,
                          borderRadius: 0.5,
                          backgroundColor: getCellBgColor(v),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'default',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: 9, fontWeight: 'bold', color: getCellTextColor(v) }}
                        >
                          {v}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {item.activeCount} de {item.total} canales activos ({Math.round(item.coverageRatio)}%)
                  </Typography>
                  <Chip
                    label={`Promedio: ${item.avg}%`}
                    size="small"
                    sx={{ backgroundColor: getCellBgColor(item.avg), fontSize: 11 }}
                  />
                </Box>
              </Box>
            ))}

            {/* Summary */}
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
              }}
            >
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Resultado de la campaña
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cobertura: {implementedCount} / {EXAMPLE_STATS.length} países (
                {Math.round((implementedCount / EXAMPLE_STATS.length) * 100)}%)
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Promedio global: {globalAvg}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={globalAvg}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#bbdefb',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: globalAvg >= 80 ? '#4CAF50' : globalAvg >= 50 ? '#FF9800' : '#f44336',
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── 4. TABLA DETALLE ────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Resumen del ejemplo
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>País</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="Canales con implementation_percentage ≥ 50 sobre el total de canales válidos" arrow>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          Canales activos
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="Porcentaje de canales con % > 0. Si ≥ 50%, el país se considera implementado." arrow>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          % Canales activos
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="Promedio de los valores de implementation_percentage de todos los canales válidos del país." arrow>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          Promedio de implementación
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>¿Implementado?</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {EXAMPLE_STATS.map((item) => (
                    <TableRow key={item.country} hover>
                      <TableCell>{item.country}</TableCell>
                      <TableCell align="center">
                        {item.activeCount} / {item.total}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${Math.round(item.coverageRatio)}%`}
                          size="small"
                          color={item.implemented ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                          <LinearProgress
                            variant="determinate"
                            value={item.avg}
                            sx={{
                              width: 80,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getCellBgColor(item.avg),
                              },
                            }}
                          />
                          <Typography variant="body2">{item.avg}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {item.implemented ? (
                          <CheckCircleIcon sx={{ color: '#4caf50' }} />
                        ) : (
                          <CancelIcon sx={{ color: '#f44336' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* ── 5. NOTAS ────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Alert severity="warning" icon={<InfoOutlinedIcon />}>
            <Typography variant="body2" component="div">
              <strong>Puntos clave a recordar:</strong>
              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                <li>El cálculo se realiza <strong>por ToolBox</strong>, no globalmente.</li>
                <li>
                  Un canal con <strong>1%</strong> y uno con <strong>99%</strong> pesan{' '}
                  <em>igual</em> al evaluar si un país supera el umbral del 50% de canales activos.
                  La diferencia aparece solo en el <strong>promedio</strong>.
                </li>
                <li>
                  Un país puede estar implementado para una ToolBox pero no para otra.
                </li>
                <li>
                  Solo se consideran canales activos tanto para el país como para la ToolBox.
                </li>
              </Box>
            </Typography>
          </Alert>
        </Grid>

      </Grid>
    </Box>
  );
};

export default CampaignCompleteness;
